import React, {useEffect, useState} from 'react';
import {useAlert} from 'src/shared/hooks/useAlert';

import {Button} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import type {NoteModuleEnum, NoteSimpleResponse} from 'src/api/ProactApiClient';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {
  useCreateNoteMutation,
  useUpdateNoteMutation,
} from 'src/redux/queries/note';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface NoteFormProps {
  mode: 'create' | 'edit' | 'view';
  entityId: number;
  noteType: NoteModuleEnum;
  data?: NoteSimpleResponse;
  onSaved: () => void;
  onCancelled: () => void;
}

const NOTE_FORM_META: IFormFieldV2[] = [
  {
    name: 'content',
    label: 'Comment',
    type: 'textarea',
    required: true,
  },
];

export const NoteForm: React.FC<NoteFormProps> = ({
  mode,
  entityId,
  noteType,
  data,
  onSaved,
  onCancelled,
}) => {
  const {setAlert} = useAlert();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [createNote] = useCreateNoteMutation();
  const [updateNote] = useUpdateNoteMutation();

  const form = useProactForm({formMeta: NOTE_FORM_META});
  const {resetForm} = form;

  useEffect(() => {
    resetForm(toFormDataV2({content: data?.content ?? ''}));
  }, [data?.content, resetForm]);

  const handleSave = async () => {
    const isValid = await form.validateForm();
    if (!isValid) {
      return;
    }

    try {
      setIsLoading(true);
      const values = form.methods.getValues();
      const request = {
        id: data?.id,
        noteType: noteType,
        entityId: entityId,
        content: values.content as string,
      };

      if (mode === 'create') {
        await createNote(request).unwrap();
      } else {
        await updateNote(request).unwrap();
      }

      resetForm(toFormDataV2({content: ''}));
      onSaved();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsLoading(false);
  };

  return (
    <Box maw={{base: '100%', md: '35rem', lg: '60rem', xl: '70rem'}}>
      {mode !== 'view' ? (
        <ProactForm form={form}>
          <Grid gutter="md">
            <ProactField name="content" />
          </Grid>
          <Group>
            <Button onClick={handleSave} size="sm" loading={isLoading}>
              Save
            </Button>
            {mode === 'edit' && (
              <Button
                onClick={onCancelled}
                size="sm"
                variant="subtle"
                color="black">
                Cancel
              </Button>
            )}
          </Group>
        </ProactForm>
      ) : (
        <FormViewRow label="" value={data?.content ?? ''} />
      )}
    </Box>
  );
};
